import React, { useEffect, useRef, useState, useCallback } from 'react';
import { HashRouter as Router } from 'react-router-dom';
import api, { setOnAuthExpired, setOnKickedOut } from './api';
import { LoginModal } from './components/Common';
import AppRoutes from './router';
import {
  formatRelativeTime,
  formatExactTime,
  playNotificationSound as _playNotificationSound,
  markNotificationRead as _markNotificationRead,
  renderNotifIcon
} from './utils/notification';

const KickedOutModal = ({ msg, onConfirm }) => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onConfirm();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [onConfirm]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 99999, backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: '#1a1a2e', border: '1px solid rgba(239,68,68,0.4)',
        borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%',
        textAlign: 'center', boxShadow: '0 0 40px rgba(239,68,68,0.15)'
      }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'rgba(239,68,68,0.15)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', fontSize: '28px'
        }}>
          ⚠️
        </div>
        <h3 style={{
          color: '#f87171', fontSize: '18px', fontWeight: 600,
          marginBottom: '12px'
        }}>账号已在其他设备登录</h3>
        <p style={{
          color: '#94a3b8', fontSize: '14px', lineHeight: '1.6',
          marginBottom: '8px'
        }}>{msg || '您的账号已在其他设备登录，如非本人操作请及时修改密码'}</p>
        <p style={{
          color: '#64748b', fontSize: '12px', marginBottom: '24px'
        }}>{countdown}秒后自动退出登录</p>
        <button
          onClick={onConfirm}
          style={{
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: '#fff', border: 'none', borderRadius: '8px',
            padding: '10px 32px', fontSize: '14px', fontWeight: 500,
            cursor: 'pointer', width: '100%',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={e => e.target.style.opacity = '0.9'}
          onMouseLeave={e => e.target.style.opacity = '1'}
        >
          确定
        </button>
      </div>
    </div>
  );
};

const App = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      if (!savedUser) return false;
      const parsed = JSON.parse(savedUser);
      if (!parsed.user_id || !parsed.token) {
        localStorage.removeItem('user');
        return false;
      }
      return true;
    } catch {
      localStorage.removeItem('user');
      return false;
    }
  });
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      if (!savedUser) return null;
      const parsed = JSON.parse(savedUser);
      if (!parsed.user_id || !parsed.token) {
        localStorage.removeItem('user');
        return null;
      }
      return parsed;
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  });

  const [globalNotifications, setGlobalNotifications] = useState([]);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);

  const [kickedOutMsg, setKickedOutMsg] = useState(null);
  const kickedOutTimerRef = useRef(null);

  const doForceLogout = useCallback(() => {
    if (kickedOutTimerRef.current) {
      clearTimeout(kickedOutTimerRef.current);
      kickedOutTimerRef.current = null;
    }
    setKickedOutMsg(null);
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem('user');
    window.location.hash = '#/';
  }, []);

  const handleKickedOut = useCallback((msg) => {
    if (kickedOutMsg) return;
    setKickedOutMsg(msg || '您的账号已在其他设备登录，如非本人操作请及时修改密码');
    setIsLoggedIn(false);
    setUser(null);
  }, [kickedOutMsg]);

  useEffect(() => {
    setOnAuthExpired((code, msg) => {
      setIsLoggedIn(false);
      setUser(null);
      localStorage.removeItem('user');
      window.location.hash = '#/';
    });
    setOnKickedOut(handleKickedOut);
    return () => {
      setOnAuthExpired(null);
      setOnKickedOut(null);
    };
  }, [handleKickedOut]);

  useEffect(() => {
    const handleStreamKickedOut = (e) => {
      handleKickedOut(e.detail);
    };
    window.addEventListener('kicked-out', handleStreamKickedOut);
    return () => window.removeEventListener('kicked-out', handleStreamKickedOut);
  }, [handleKickedOut]);

  useEffect(() => {
    if (user?.user_id) {
      api.notification.getAll().then(result => {
        if (result.success && result.data) {
          setGlobalNotifications(result.data.notifications || []);
          setNotifUnreadCount(result.data.unread_count || 0);
        }
      }).catch(() => {});
    } else {
      setGlobalNotifications([]);
      setNotifUnreadCount(0);
    }
  }, [user]);

  const refreshNotifications = useCallback(() => {
    if (user?.user_id) {
      api.notification.getAll().then(result => {
        if (result.success && result.data) {
          setGlobalNotifications(result.data.notifications || []);
          setNotifUnreadCount(result.data.unread_count || 0);
        }
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!user?.user_id) return;
    refreshNotifications();
    const timer = setInterval(refreshNotifications, 15000);
    return () => clearInterval(timer);
  }, [user, refreshNotifications]);

  const [notifSoundEnabled, setNotifSoundEnabled] = useState(() => {
    try { return localStorage.getItem('notif_sound') !== 'false'; } catch { return true; }
  });

  const audioCtxRef = useRef(null);
  const audioCtxReady = useRef(false);

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  }, []);

  useEffect(() => {
    const activateAudio = () => {
      if (audioCtxReady.current) return;
      audioCtxReady.current = true;
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        audioCtxRef.current = ctx;
      } catch (e) {}
    };
    document.addEventListener('click', activateAudio, { once: true });
    document.addEventListener('keydown', activateAudio, { once: true });
    document.addEventListener('touchstart', activateAudio, { once: true });
    return () => {
      document.removeEventListener('click', activateAudio);
      document.removeEventListener('keydown', activateAudio);
      document.removeEventListener('touchstart', activateAudio);
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    _playNotificationSound(notifSoundEnabled, getAudioContext);
  }, [notifSoundEnabled, getAudioContext]);

  const prevUnreadCountRef = useRef(-1);
  const notifInitializedRef = useRef(false);

  useEffect(() => {
    if (!notifInitializedRef.current) {
      notifInitializedRef.current = true;
      prevUnreadCountRef.current = notifUnreadCount;
      return;
    }
    if (notifUnreadCount > prevUnreadCountRef.current) {
      playNotificationSound();
    }
    prevUnreadCountRef.current = notifUnreadCount;
  }, [notifUnreadCount, playNotificationSound]);

  const markNotificationRead = useCallback((notifId, source) => {
    _markNotificationRead(notifId, source, setGlobalNotifications, setNotifUnreadCount);
  }, []);

  const handleLogin = (userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    setIsLoginModalOpen(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem('user');
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        if (e.newValue) {
          try {
            const userData = JSON.parse(e.newValue);
            setIsLoggedIn(true);
            setUser(userData);
          } catch {}
        } else {
          setIsLoggedIn(false);
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <Router>
      {kickedOutMsg && (
        <KickedOutModal msg={kickedOutMsg} onConfirm={doForceLogout} />
      )}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
      />
      <AppRoutes
        onLoginClick={() => setIsLoginModalOpen(true)}
        isLoggedIn={isLoggedIn}
        user={user}
        onLogout={handleLogout}
        onUserUpdate={setUser}
        formatRelativeTime={formatRelativeTime}
        formatExactTime={formatExactTime}
        markNotificationRead={markNotificationRead}
        renderNotifIcon={renderNotifIcon}
        globalNotifications={globalNotifications}
        notifUnreadCount={notifUnreadCount}
        notifSoundEnabled={notifSoundEnabled}
        setNotifSoundEnabled={setNotifSoundEnabled}
        refreshNotifications={refreshNotifications}
      />
    </Router>
  );
};

export default App;
